/**
 * Connector Registry — master catalog of all available integrations.
 *
 * Each connector defines:
 *   - Metadata: id, name, category, icon/emoji, description
 *   - Auth: type (oauth2, api_key, basic, certificate), field definitions
 *   - Direction: 'ingest', 'export', or 'both'
 *   - Transport: the underlying protocol (rest, sftp, as2, s3, etc.)
 *   - Status: 'available' (backend exists), 'coming_soon', or 'beta'
 *
 * The Pipeline Builder wizard reads this registry to render branded tiles
 * and dynamically generate credential forms.
 */

import logo_s3 from './images/logos/amazon-s3.png';
import logo_azure from './images/logos/azure-blob-storage.png';
import logo_gcp from './images/logos/google-cloud-storage.png';
import logo_minio from './images/logos/minio.png';
import logo_sftp from './images/logos/sftp.png';
import logo_ftp from './images/logos/ftp.png';
import logo_as2 from './images/logos/as2.png';
import logo_webhook from './images/logos/webhook.png';
import logo_shopify from './images/logos/shopify.png';
import logo_amazon_sp from './images/logos/amazon-sp-api.png';
import logo_amazon_vc from './images/logos/amazon-vendor-central.png';
import logo_woocommerce from './images/logos/woocommerce.png';
import logo_bigcommerce from './images/logos/bigcommerce.png';
import logo_magento from './images/logos/adobe-commerce-magento.png';
import logo_netsuite from './images/logos/netsuite.png';
import logo_quickbooks from './images/logos/quickbooks-online.png';
import logo_sap_b1 from './images/logos/sap-business-one.png';
import logo_dynamics365 from './images/logos/dynamics-365.png';
import logo_odoo from './images/logos/odoo.png';
import logo_xero from './images/logos/xero.png';
import logo_sps_commerce from './images/logos/sps-commerce.png';
import logo_truecommerce from './images/logos/truecommerce.png';
import logo_cleo from './images/logos/cleo-integration.png';
import logo_opentext from './images/logos/opentext-b2b.png';
import logo_stedi from './images/logos/stedi.png';
import logo_shipstation from './images/logos/shipstation.png';
import logo_shipbob from './images/logos/shipbob.png';
import logo_flexport from './images/logos/flexport.png';
import logo_slack from './images/logos/slack.png';
import logo_teams from './images/logos/microsoft-teams.png';
import logo_pagerduty from './images/logos/pagerduty.png';
import logo_email_smtp from './images/logos/email-smtp.png';
import logo_jira from './images/logos/jira.png';
import logo_github from './images/logos/github.png';
import logo_datadog from './images/logos/datadog.png';
import logo_webhook_export from './images/logos/custom-webhook.png';
import logo_salesforce from './images/logos/salesforce.png';
import logo_hubspot from './images/logos/hubspot.png';
import logo_zoho_crm from './images/logos/zoho-crm.png';
import logo_pipedrive from './images/logos/pipedrive.png';
import logo_stripe from './images/logos/stripe.png';
import logo_square from './images/logos/square.png';
import logo_paypal from './images/logos/paypal.png';
import logo_braintree from './images/logos/braintree.png';
import logo_adyen from './images/logos/adyen.png';
import logo_hl7_fhir from './images/logos/hl7-fhir.png';
import logo_x12_hipaa from './images/logos/x12-hipaa.png';
import logo_epic from './images/logos/epic-ehr.png';
import logo_cerner from './images/logos/oracle-health-cerner.png';
import logo_availity from './images/logos/availity.png';
import logo_change_healthcare from './images/logos/change-healthcare.png';
import logo_postgresql from './images/logos/postgresql.png';
import logo_mysql from './images/logos/mysql.png';
import logo_mongodb from './images/logos/mongodb.png';
import logo_snowflake from './images/logos/snowflake.png';
import logo_bigquery from './images/logos/bigquery.png';
import logo_redshift from './images/logos/amazon-redshift.png';
import logo_fedex from './images/logos/fedex.png';
import logo_ups from './images/logos/ups.png';
import logo_usps from './images/logos/usps.png';
import logo_dhl from './images/logos/dhl.png';
import logo_easypost from './images/logos/easypost.png';
import logo_ebay from './images/logos/ebay.png';
import logo_etsy from './images/logos/etsy.png';
import logo_walmart from './images/logos/walmart-marketplace.png';
import logo_commport from './images/logos/commport.png';
import logo_inovis from './images/logos/opentext-trading-grid.png';
import logo_kleinschmidt from './images/logos/kleinschmidt-1edi.png';
import logo_shiphero from './images/logos/shiphero.png';
import logo_skubana from './images/logos/extensiv-skubana.png';
import logo_discord from './images/logos/discord.png';
import logo_opsgenie from './images/logos/opsgenie.png';
import logo_twilio from './images/logos/twilio-sms.png';
import logo_sage from './images/logos/sage-intacct.png';
import logo_freshbooks from './images/logos/freshbooks.png';
import logo_splunk from './images/logos/splunk.png';
import logo_grafana from './images/logos/grafana.png';
import logo_newrelic from './images/logos/new-relic.png';
import logo_linear from './images/logos/linear.png';
import logo_zapier from './images/logos/zapier.png';
import logo_make from './images/logos/make-integromat.png';

// ─── Categories ──────────────────────────────────────────────────────────────

export const CONNECTOR_CATEGORIES = [
    { id: 'cloud', label: 'Cloud Storage', icon: '', color: '#60a5fa' },
    { id: 'protocol', label: 'File Transfer', icon: '', color: '#a78bfa' },
    { id: 'ecommerce', label: 'E-Commerce', icon: '', color: '#34d399' },
    { id: 'erp', label: 'ERP / Finance', icon: '', color: '#fbbf24' },
    { id: 'edi_van', label: 'EDI / VAN', icon: '', color: '#f87171' },
    { id: 'wms', label: 'WMS / 3PL', icon: '', color: '#fb923c' },
    { id: 'crm', label: 'CRM', icon: '', color: '#38bdf8' },
    { id: 'payments', label: 'Payments', icon: '', color: '#a3e635' },
    { id: 'healthcare', label: 'Healthcare', icon: '', color: '#f472b6' },
    { id: 'database', label: 'Databases', icon: '', color: '#c084fc' },
    { id: 'logistics', label: 'Logistics', icon: '', color: '#fdba74' },
    { id: 'notification', label: 'Notifications', icon: '', color: '#818cf8' },
    { id: 'devops', label: 'Dev / Ops', icon: '', color: '#94a3b8' },
];

// ─── Field Types ──────────────────────────────────────────────────────────────

// Field definition shape:
// { key, label, type: 'text'|'password'|'number'|'select'|'textarea', required, placeholder, options? }

// ─── Connector Definitions ───────────────────────────────────────────────────

export const CONNECTORS = [
    // ═══════════════════════════════════════════
    // Cloud Storage (already built)
    // ═══════════════════════════════════════════
    {
        id: 's3',
        name: 'Amazon S3',
        category: 'cloud',
        logo: logo_s3,
        brandColor: '#FF9900',
        description: 'S3 bucket polling, file ingestion, and drift detection.',
        direction: 'both',
        transport: 's3',
        status: 'available',
        authType: 'api_key',
        fields: [
            { key: 'bucket_name', label: 'Bucket Name', type: 'text', required: true, placeholder: 'my-edi-bucket' },
            { key: 'access_key', label: 'Access Key ID', type: 'text', required: true },
            { key: 'secret_key', label: 'Secret Access Key', type: 'password', required: true },
            { key: 'region', label: 'Region', type: 'text', required: false, placeholder: 'us-east-1' },
            { key: 'prefix', label: 'Path Prefix', type: 'text', required: false, placeholder: 'inbound/edi/' },
            { key: 'file_pattern', label: 'File Pattern', type: 'text', required: false, placeholder: '*.json' },
        ],
    },
    {
        id: 'azure',
        name: 'Azure Blob Storage',
        category: 'cloud',
        logo: logo_azure,
        brandColor: '#0078D4',
        description: 'Azure Blob container monitoring and file ingestion.',
        direction: 'both',
        transport: 'azure',
        status: 'available',
        authType: 'api_key',
        fields: [
            { key: 'bucket_name', label: 'Container Name', type: 'text', required: true },
            { key: 'connection_string', label: 'Connection String', type: 'password', required: true },
            { key: 'prefix', label: 'Blob Prefix', type: 'text', required: false },
            { key: 'file_pattern', label: 'File Pattern', type: 'text', required: false, placeholder: '*.json' },
        ],
    },
    {
        id: 'gcp',
        name: 'Google Cloud Storage',
        category: 'cloud',
        logo: logo_gcp,
        brandColor: '#4285F4',
        description: 'GCS bucket polling and file change detection.',
        direction: 'both',
        transport: 'gcp',
        status: 'available',
        authType: 'api_key',
        fields: [
            { key: 'bucket_name', label: 'Bucket Name', type: 'text', required: true },
            { key: 'project_id', label: 'Project ID', type: 'text', required: true },
            { key: 'service_account_json', label: 'Service Account JSON', type: 'textarea', required: true },
            { key: 'prefix', label: 'Path Prefix', type: 'text', required: false },
        ],
    },
    {
        id: 'minio',
        name: 'MinIO',
        category: 'cloud',
        logo: logo_minio,
        brandColor: '#C72C48',
        description: 'S3-compatible object storage for on-prem deployments.',
        direction: 'both',
        transport: 's3',
        status: 'available',
        authType: 'api_key',
        fields: [
            { key: 'bucket_name', label: 'Bucket Name', type: 'text', required: true },
            { key: 'access_key', label: 'Access Key', type: 'text', required: true },
            { key: 'secret_key', label: 'Secret Key', type: 'password', required: true },
            { key: 'endpoint_url', label: 'Endpoint URL', type: 'text', required: true, placeholder: 'http://minio:9000' },
        ],
    },

    // ═══════════════════════════════════════════
    // File Transfer Protocols (already built)
    // ═══════════════════════════════════════════
    {
        id: 'sftp',
        name: 'SFTP',
        category: 'protocol',
        logo: logo_sftp,
        brandColor: '#22c55e',
        description: 'Encrypted file transfer for partner data exchange.',
        direction: 'both',
        transport: 'sftp',
        status: 'available',
        authType: 'basic',
        fields: [
            { key: 'host', label: 'Host', type: 'text', required: true, placeholder: 'sftp.partner.com' },
            { key: 'port', label: 'Port', type: 'number', required: false, placeholder: '22' },
            { key: 'username', label: 'Username', type: 'text', required: true },
            { key: 'password', label: 'Password', type: 'password', required: false },
            { key: 'private_key', label: 'Private Key (PEM)', type: 'textarea', required: false },
            { key: 'prefix', label: 'Remote Directory', type: 'text', required: false, placeholder: '/inbound/' },
        ],
    },
    {
        id: 'ftp',
        name: 'FTP',
        category: 'protocol',
        logo: logo_ftp,
        brandColor: '#eab308',
        description: 'Standard FTP with optional TLS for legacy connections.',
        direction: 'both',
        transport: 'ftp',
        status: 'available',
        authType: 'basic',
        fields: [
            { key: 'host', label: 'Host', type: 'text', required: true },
            { key: 'port', label: 'Port', type: 'number', required: false, placeholder: '21' },
            { key: 'username', label: 'Username', type: 'text', required: true },
            { key: 'password', label: 'Password', type: 'password', required: true },
            { key: 'use_tls', label: 'Use TLS', type: 'select', required: false, options: [{ value: 'false', label: 'No' }, { value: 'true', label: 'Yes' }] },
        ],
    },
    {
        id: 'as2',
        name: 'AS2',
        category: 'protocol',
        logo: logo_as2,
        brandColor: '#6366f1',
        description: 'AS2 protocol for signed and encrypted B2B EDI.',
        direction: 'both',
        transport: 'as2',
        status: 'available',
        authType: 'certificate',
        fields: [
            // AS2 uses auto-generated endpoints — no manual credential entry
        ],
    },
    {
        id: 'webhook',
        name: 'Webhook',
        category: 'protocol',
        logo: logo_webhook,
        brandColor: '#a855f7',
        description: 'HTTP POST endpoint for real-time data ingestion.',
        direction: 'ingest',
        transport: 'webhook',
        status: 'available',
        authType: 'none',
        fields: [],
    },

    // ═══════════════════════════════════════════
    // E-Commerce
    // ═══════════════════════════════════════════
    {
        id: 'shopify',
        name: 'Shopify',
        category: 'ecommerce',
        logo: logo_shopify,
        brandColor: '#96BF48',
        description: 'Orders, products, and inventory via Shopify Admin API.',
        direction: 'both',
        transport: 'rest',
        status: 'coming_soon',
        authType: 'oauth2',
        fields: [
            { key: 'store_url', label: 'Store URL', type: 'text', required: true, placeholder: 'mystore.myshopify.com' },
            { key: 'api_key', label: 'API Key', type: 'text', required: true },
            { key: 'api_secret', label: 'API Secret', type: 'password', required: true },
            { key: 'access_token', label: 'Access Token', type: 'password', required: true },
        ],
    },
    {
        id: 'amazon_sp',
        name: 'Amazon SP-API',
        category: 'ecommerce',
        logo: logo_amazon_sp,
        brandColor: '#FF9900',
        description: 'Orders, inventory, and catalog via SP-API.',
        direction: 'both',
        transport: 'rest',
        status: 'coming_soon',
        authType: 'oauth2',
        fields: [
            { key: 'marketplace_id', label: 'Marketplace ID', type: 'text', required: true },
            { key: 'refresh_token', label: 'Refresh Token', type: 'password', required: true },
            { key: 'client_id', label: 'Client ID', type: 'text', required: true },
            { key: 'client_secret', label: 'Client Secret', type: 'password', required: true },
        ],
    },
    {
        id: 'amazon_vc',
        name: 'Amazon Vendor Central',
        category: 'ecommerce',
        logo: logo_amazon_vc,
        brandColor: '#FF9900',
        description: 'Purchase orders and ASNs via EDI (850, 856, 810).',
        direction: 'both',
        transport: 'as2',
        status: 'coming_soon',
        authType: 'certificate',
        fields: [],
    },
    {
        id: 'woocommerce',
        name: 'WooCommerce',
        category: 'ecommerce',
        logo: logo_woocommerce,
        brandColor: '#96588A',
        description: 'Orders and products via WooCommerce REST API.',
        direction: 'both',
        transport: 'rest',
        status: 'coming_soon',
        authType: 'api_key',
        fields: [
            { key: 'site_url', label: 'Site URL', type: 'text', required: true, placeholder: 'https://mystore.com' },
            { key: 'consumer_key', label: 'Consumer Key', type: 'text', required: true },
            { key: 'consumer_secret', label: 'Consumer Secret', type: 'password', required: true },
        ],
    },
    {
        id: 'bigcommerce',
        name: 'BigCommerce',
        category: 'ecommerce',
        logo: logo_bigcommerce,
        brandColor: '#34313F',
        description: 'Orders and products via BigCommerce API.',
        direction: 'both',
        transport: 'rest',
        status: 'coming_soon',
        authType: 'api_key',
        fields: [
            { key: 'store_hash', label: 'Store Hash', type: 'text', required: true },
            { key: 'access_token', label: 'Access Token', type: 'password', required: true },
        ],
    },
    {
        id: 'magento',
        name: 'Adobe Commerce (Magento)',
        category: 'ecommerce',
        logo: logo_magento,
        brandColor: '#F26322',
        description: 'Orders and catalog via Adobe Commerce REST API.',
        direction: 'both',
        transport: 'rest',
        status: 'coming_soon',
        authType: 'oauth2',
        fields: [
            { key: 'base_url', label: 'Base URL', type: 'text', required: true },
            { key: 'access_token', label: 'Access Token', type: 'password', required: true },
        ],
    },

    // ═══════════════════════════════════════════
    // ERP / Finance
    // ═══════════════════════════════════════════
    {
        id: 'netsuite',
        name: 'NetSuite',
        category: 'erp',
        logo: logo_netsuite,
        brandColor: '#1A3C6E',
        description: 'POs, invoices, and inventory via SuiteTalk REST API.',
        direction: 'both',
        transport: 'rest',
        status: 'coming_soon',
        authType: 'oauth2',
        fields: [
            { key: 'account_id', label: 'Account ID', type: 'text', required: true },
            { key: 'consumer_key', label: 'Consumer Key', type: 'text', required: true },
            { key: 'consumer_secret', label: 'Consumer Secret', type: 'password', required: true },
            { key: 'token_id', label: 'Token ID', type: 'text', required: true },
            { key: 'token_secret', label: 'Token Secret', type: 'password', required: true },
        ],
    },
    {
        id: 'quickbooks',
        name: 'QuickBooks Online',
        category: 'erp',
        logo: logo_quickbooks,
        brandColor: '#2CA01C',
        description: 'Invoices, payments, and accounting data via Intuit API.',
        direction: 'both',
        transport: 'rest',
        status: 'coming_soon',
        authType: 'oauth2',
        fields: [
            { key: 'company_id', label: 'Company ID', type: 'text', required: true },
            { key: 'client_id', label: 'Client ID', type: 'text', required: true },
            { key: 'client_secret', label: 'Client Secret', type: 'password', required: true },
        ],
    },
    {
        id: 'sap_b1',
        name: 'SAP Business One',
        category: 'erp',
        logo: logo_sap_b1,
        brandColor: '#0FAAFF',
        description: 'Orders and financials via SAP B1 Service Layer.',
        direction: 'both',
        transport: 'rest',
        status: 'coming_soon',
        authType: 'basic',
        fields: [
            { key: 'service_url', label: 'Service Layer URL', type: 'text', required: true },
            { key: 'company_db', label: 'Company Database', type: 'text', required: true },
            { key: 'username', label: 'Username', type: 'text', required: true },
            { key: 'password', label: 'Password', type: 'password', required: true },
        ],
    },
    {
        id: 'dynamics365',
        name: 'Dynamics 365',
        category: 'erp',
        logo: logo_dynamics365,
        brandColor: '#002050',
        description: 'Finance and operations data via Dynamics 365 API.',
        direction: 'both',
        transport: 'rest',
        status: 'coming_soon',
        authType: 'oauth2',
        fields: [
            { key: 'tenant_id', label: 'Tenant ID', type: 'text', required: true },
            { key: 'client_id', label: 'Client ID', type: 'text', required: true },
            { key: 'client_secret', label: 'Client Secret', type: 'password', required: true },
            { key: 'environment_url', label: 'Environment URL', type: 'text', required: true },
        ],
    },
    {
        id: 'odoo',
        name: 'Odoo',
        category: 'erp',
        logo: logo_odoo,
        brandColor: '#875A7B',
        description: 'Sales, inventory, and accounting via Odoo XML-RPC API.',
        direction: 'both',
        transport: 'rest',
        status: 'coming_soon',
        authType: 'api_key',
        fields: [
            { key: 'url', label: 'Odoo URL', type: 'text', required: true },
            { key: 'database', label: 'Database', type: 'text', required: true },
            { key: 'username', label: 'Username', type: 'text', required: true },
            { key: 'api_key', label: 'API Key', type: 'password', required: true },
        ],
    },
    {
        id: 'xero',
        name: 'Xero',
        category: 'erp',
        logo: logo_xero,
        brandColor: '#13B5EA',
        description: 'Invoices, bills, and bank reconciliation via Xero API.',
        direction: 'both',
        transport: 'rest',
        status: 'coming_soon',
        authType: 'oauth2',
        fields: [
            { key: 'client_id', label: 'Client ID', type: 'text', required: true },
            { key: 'client_secret', label: 'Client Secret', type: 'password', required: true },
        ],
    },

    // ═══════════════════════════════════════════
    // EDI / VAN
    // ═══════════════════════════════════════════
    {
        id: 'sps_commerce',
        name: 'SPS Commerce',
        category: 'edi_van',
        logo: logo_sps_commerce,
        brandColor: '#E4002B',
        description: 'Retail supply chain EDI network via AS2.',
        direction: 'both',
        transport: 'as2',
        status: 'available',
        authType: 'certificate',
        fields: [],
    },
    {
        id: 'truecommerce',
        name: 'TrueCommerce',
        category: 'edi_van',
        logo: logo_truecommerce,
        brandColor: '#0072CE',
        description: 'EDI and supply chain connectivity via AS2.',
        direction: 'both',
        transport: 'as2',
        status: 'available',
        authType: 'certificate',
        fields: [],
    },
    {
        id: 'cleo',
        name: 'Cleo Integration',
        category: 'edi_van',
        logo: logo_cleo,
        brandColor: '#00B4D8',
        description: 'B2B data exchange and ecosystem integration via AS2.',
        direction: 'both',
        transport: 'as2',
        status: 'available',
        authType: 'api_key',
        fields: [],
    },
    {
        id: 'opentext',
        name: 'OpenText B2B',
        category: 'edi_van',
        logo: logo_opentext,
        brandColor: '#333333',
        description: 'Enterprise B2B integration and managed file transfer.',
        direction: 'both',
        transport: 'as2',
        status: 'available',
        authType: 'certificate',
        fields: [],
    },
    {
        id: 'stedi',
        name: 'Stedi',
        category: 'edi_van',
        logo: logo_stedi,
        brandColor: '#00DC82',
        description: 'API-first X12 EDI parsing and document management.',
        direction: 'both',
        transport: 'rest',
        status: 'coming_soon',
        authType: 'api_key',
        fields: [
            { key: 'api_key', label: 'API Key', type: 'password', required: true },
        ],
    },

    // ═══════════════════════════════════════════
    // WMS / 3PL
    // ═══════════════════════════════════════════
    {
        id: 'shipstation',
        name: 'ShipStation',
        category: 'wms',
        logo: logo_shipstation,
        brandColor: '#78BE20',
        description: 'Shipping labels, tracking, and order fulfillment.',
        direction: 'export',
        transport: 'rest',
        status: 'coming_soon',
        authType: 'api_key',
        fields: [
            { key: 'api_key', label: 'API Key', type: 'text', required: true },
            { key: 'api_secret', label: 'API Secret', type: 'password', required: true },
        ],
    },
    {
        id: 'shipbob',
        name: 'ShipBob',
        category: 'wms',
        logo: logo_shipbob,
        brandColor: '#5C2D91',
        description: 'Warehouse management and order fulfillment for D2C.',
        direction: 'both',
        transport: 'rest',
        status: 'coming_soon',
        authType: 'oauth2',
        fields: [],
    },
    {
        id: 'flexport',
        name: 'Flexport',
        category: 'wms',
        logo: logo_flexport,
        brandColor: '#000E52',
        description: 'Freight forwarding and supply chain visibility.',
        direction: 'both',
        transport: 'rest',
        status: 'coming_soon',
        authType: 'api_key',
        fields: [
            { key: 'api_key', label: 'API Key', type: 'password', required: true },
        ],
    },

    // ═══════════════════════════════════════════
    // Notifications
    // ═══════════════════════════════════════════
    {
        id: 'slack',
        name: 'Slack',
        category: 'notification',
        logo: logo_slack,
        brandColor: '#4A154B',
        description: 'Drift alerts and pipeline status to Slack channels.',
        direction: 'export',
        transport: 'webhook',
        status: 'available',
        authType: 'api_key',
        fields: [
            { key: 'webhook_url', label: 'Webhook URL', type: 'text', required: true, placeholder: 'https://hooks.slack.com/services/...' },
        ],
    },
    {
        id: 'teams',
        name: 'Microsoft Teams',
        category: 'notification',
        logo: logo_teams,
        brandColor: '#6264A7',
        description: 'Drift alerts delivered to Teams channels.',
        direction: 'export',
        transport: 'webhook',
        status: 'available',
        authType: 'api_key',
        fields: [
            { key: 'webhook_url', label: 'Incoming Webhook URL', type: 'text', required: true },
        ],
    },
    {
        id: 'pagerduty',
        name: 'PagerDuty',
        category: 'notification',
        logo: logo_pagerduty,
        brandColor: '#06AC38',
        description: 'Incident creation on critical schema drift events.',
        direction: 'export',
        transport: 'rest',
        status: 'available',
        authType: 'api_key',
        fields: [
            { key: 'routing_key', label: 'Routing Key', type: 'password', required: true },
        ],
    },
    {
        id: 'email_smtp',
        name: 'Email (SMTP)',
        category: 'notification',
        logo: logo_email_smtp,
        brandColor: '#EA4335',
        description: 'Drift event notifications delivered via email.',
        direction: 'export',
        transport: 'smtp',
        status: 'available',
        authType: 'basic',
        fields: [
            { key: 'smtp_host', label: 'SMTP Host', type: 'text', required: true },
            { key: 'smtp_port', label: 'Port', type: 'number', required: true, placeholder: '587' },
            { key: 'username', label: 'Username', type: 'text', required: true },
            { key: 'password', label: 'Password', type: 'password', required: true },
            { key: 'to_email', label: 'To Email', type: 'text', required: true },
        ],
    },

    // ═══════════════════════════════════════════
    // Dev / Ops
    // ═══════════════════════════════════════════
    {
        id: 'jira',
        name: 'Jira',
        category: 'devops',
        logo: logo_jira,
        brandColor: '#0052CC',
        description: 'Auto-create Jira tickets on schema drift events.',
        direction: 'export',
        transport: 'rest',
        status: 'available',
        authType: 'api_key',
        fields: [
            { key: 'base_url', label: 'Jira URL', type: 'text', required: true, placeholder: 'https://yourcompany.atlassian.net' },
            { key: 'email', label: 'Email', type: 'text', required: true },
            { key: 'api_token', label: 'API Token', type: 'password', required: true },
            { key: 'project_key', label: 'Project Key', type: 'text', required: true, placeholder: 'EDI' },
        ],
    },
    {
        id: 'github',
        name: 'GitHub',
        category: 'devops',
        logo: logo_github,
        brandColor: '#333333',
        description: 'Auto-create GitHub Issues on drift events.',
        direction: 'export',
        transport: 'rest',
        status: 'available',
        authType: 'api_key',
        fields: [
            { key: 'token', label: 'Personal Access Token', type: 'password', required: true },
            { key: 'owner', label: 'Repository Owner', type: 'text', required: true },
            { key: 'repo', label: 'Repository Name', type: 'text', required: true },
        ],
    },
    {
        id: 'datadog',
        name: 'Datadog',
        category: 'devops',
        logo: logo_datadog,
        brandColor: '#632CA6',
        description: 'Drift metrics and events forwarded to Datadog.',
        direction: 'export',
        transport: 'rest',
        status: 'available',
        authType: 'api_key',
        fields: [
            { key: 'api_key', label: 'API Key', type: 'password', required: true },
            {
                key: 'site', label: 'Datadog Site', type: 'select', required: false, options: [
                    { value: 'datadoghq.com', label: 'US1 (datadoghq.com)' },
                    { value: 'datadoghq.eu', label: 'EU (datadoghq.eu)' },
                    { value: 'us3.datadoghq.com', label: 'US3' },
                    { value: 'us5.datadoghq.com', label: 'US5' },
                ]
            },
        ],
    },
    {
        id: 'webhook_export',
        name: 'Custom Webhook',
        category: 'devops',
        logo: logo_webhook_export,
        brandColor: '#6b7280',
        description: 'POST drift event payloads to any HTTP endpoint.',
        direction: 'export',
        transport: 'webhook',
        status: 'available',
        authType: 'api_key',
        fields: [
            { key: 'url', label: 'Webhook URL', type: 'text', required: true, placeholder: 'https://api.example.com/drift-events' },
            { key: 'auth_header', label: 'Auth Header Value', type: 'password', required: false, placeholder: 'Bearer xxxx' },
        ],
    },

    // ═══════════════════════════════════════════
    // CRM
    // ═══════════════════════════════════════════
    {
        id: 'salesforce',
        name: 'Salesforce',
        category: 'crm',
        logo: logo_salesforce,
        brandColor: '#00A1E0',
        description: 'Accounts, contacts, and custom object metadata via REST.',
        direction: 'both',
        transport: 'rest',
        status: 'available',
        authType: 'oauth2',
        fields: [
            { key: 'instance_url', label: 'Instance URL', type: 'text', required: true, placeholder: 'https://myorg.salesforce.com' },
            { key: 'client_id', label: 'Consumer Key', type: 'text', required: true },
            { key: 'client_secret', label: 'Consumer Secret', type: 'password', required: true },
            { key: 'refresh_token', label: 'Refresh Token', type: 'password', required: true },
        ],
    },
    {
        id: 'hubspot',
        name: 'HubSpot',
        category: 'crm',
        logo: logo_hubspot,
        brandColor: '#FF7A59',
        description: 'Contacts, deals, and tickets via HubSpot API.',
        direction: 'both',
        transport: 'rest',
        status: 'coming_soon',
        authType: 'api_key',
        fields: [
            { key: 'access_token', label: 'Private App Access Token', type: 'password', required: true },
        ],
    },
    {
        id: 'zoho_crm',
        name: 'Zoho CRM',
        category: 'crm',
        logo: logo_zoho_crm,
        brandColor: '#E42527',
        description: 'Leads, contacts, and deals via Zoho CRM API.',
        direction: 'both',
        transport: 'rest',
        status: 'coming_soon',
        authType: 'oauth2',
        fields: [
            { key: 'client_id', label: 'Client ID', type: 'text', required: true },
            { key: 'client_secret', label: 'Client Secret', type: 'password', required: true },
            { key: 'refresh_token', label: 'Refresh Token', type: 'password', required: true },
            {
                key: 'region', label: 'Data Center', type: 'select', required: true, options: [
                    { value: 'com', label: 'US (.com)' },
                    { value: 'eu', label: 'EU (.eu)' },
                    { value: 'in', label: 'India (.in)' },
                    { value: 'com.au', label: 'Australia (.com.au)' },
                ]
            },
        ],
    },
    {
        id: 'pipedrive',
        name: 'Pipedrive',
        category: 'crm',
        logo: logo_pipedrive,
        brandColor: '#017737',
        description: 'Deals, contacts, and activities via Pipedrive API.',
        direction: 'both',
        transport: 'rest',
        status: 'coming_soon',
        authType: 'api_key',
        fields: [
            { key: 'api_token', label: 'API Token', type: 'password', required: true },
            { key: 'company_domain', label: 'Company Domain', type: 'text', required: true, placeholder: 'yourcompany.pipedrive.com' },
        ],
    },

    // ═══════════════════════════════════════════
    // Payments
    // ═══════════════════════════════════════════
    {
        id: 'stripe',
        name: 'Stripe',
        category: 'payments',
        logo: logo_stripe,
        brandColor: '#635BFF',
        description: 'Invoices, subscriptions, and payout data via Stripe API.',
        direction: 'both',
        transport: 'rest',
        status: 'coming_soon',
        authType: 'api_key',
        fields: [
            { key: 'api_key', label: 'Secret Key', type: 'password', required: true, placeholder: 'sk_live_...' },
        ],
    },
    {
        id: 'square',
        name: 'Square',
        category: 'payments',
        logo: logo_square,
        brandColor: '#006AFF',
        description: 'POS, invoicing, and payment data via Square API.',
        direction: 'both',
        transport: 'rest',
        status: 'coming_soon',
        authType: 'oauth2',
        fields: [
            { key: 'access_token', label: 'Access Token', type: 'password', required: true },
            { key: 'location_id', label: 'Location ID', type: 'text', required: false },
        ],
    },
    {
        id: 'paypal',
        name: 'PayPal',
        category: 'payments',
        logo: logo_paypal,
        brandColor: '#003087',
        description: 'Transactions, invoices, and payouts via PayPal API.',
        direction: 'both',
        transport: 'rest',
        status: 'coming_soon',
        authType: 'oauth2',
        fields: [
            { key: 'client_id', label: 'Client ID', type: 'text', required: true },
            { key: 'client_secret', label: 'Client Secret', type: 'password', required: true },
            {
                key: 'environment', label: 'Environment', type: 'select', required: true, options: [
                    { value: 'sandbox', label: 'Sandbox' },
                    { value: 'live', label: 'Live' },
                ]
            },
        ],
    },
    {
        id: 'braintree',
        name: 'Braintree',
        category: 'payments',
        logo: logo_braintree,
        brandColor: '#28a745',
        description: 'Cards, wallets, and subscriptions via Braintree gateway.',
        direction: 'both',
        transport: 'rest',
        status: 'coming_soon',
        authType: 'api_key',
        fields: [
            { key: 'merchant_id', label: 'Merchant ID', type: 'text', required: true },
            { key: 'public_key', label: 'Public Key', type: 'text', required: true },
            { key: 'private_key', label: 'Private Key', type: 'password', required: true },
        ],
    },
    {
        id: 'adyen',
        name: 'Adyen',
        category: 'payments',
        logo: logo_adyen,
        brandColor: '#0ABF53',
        description: 'Enterprise payment processing and transaction data.',
        direction: 'both',
        transport: 'rest',
        status: 'coming_soon',
        authType: 'api_key',
        fields: [
            { key: 'api_key', label: 'API Key', type: 'password', required: true },
            { key: 'merchant_account', label: 'Merchant Account', type: 'text', required: true },
            {
                key: 'environment', label: 'Environment', type: 'select', required: true, options: [
                    { value: 'test', label: 'Test' },
                    { value: 'live', label: 'Live' },
                ]
            },
        ],
    },

    // ═══════════════════════════════════════════
    // Healthcare / HIPAA
    // ═══════════════════════════════════════════
    {
        id: 'hl7_fhir',
        name: 'HL7 FHIR',
        category: 'healthcare',
        logo: logo_hl7_fhir,
        brandColor: '#E34F26',
        description: 'FHIR R4 compliant API for clinical data exchange.',
        direction: 'both',
        transport: 'rest',
        status: 'coming_soon',
        authType: 'oauth2',
        fields: [
            { key: 'fhir_base_url', label: 'FHIR Server URL', type: 'text', required: true, placeholder: 'https://fhir.example.com/r4' },
            { key: 'client_id', label: 'Client ID', type: 'text', required: true },
            { key: 'client_secret', label: 'Client Secret', type: 'password', required: true },
        ],
    },
    {
        id: 'x12_hipaa',
        name: 'X12 HIPAA (837/835)',
        category: 'healthcare',
        logo: logo_x12_hipaa,
        brandColor: '#2563eb',
        description: 'Claims (837) and remittance (835) via X12 EDI.',
        direction: 'both',
        transport: 'sftp',
        status: 'available',
        authType: 'basic',
        fields: [
            { key: 'host', label: 'Clearinghouse Host', type: 'text', required: true },
            { key: 'port', label: 'Port', type: 'number', required: false, placeholder: '22' },
            { key: 'username', label: 'Username', type: 'text', required: true },
            { key: 'password', label: 'Password', type: 'password', required: true },
            { key: 'sender_id', label: 'Sender ID (ISA06)', type: 'text', required: true },
            { key: 'receiver_id', label: 'Receiver ID (ISA08)', type: 'text', required: true },
        ],
    },
    {
        id: 'epic',
        name: 'Epic EHR',
        category: 'healthcare',
        logo: logo_epic,
        brandColor: '#9D2235',
        description: 'EHR integration via FHIR and proprietary Epic APIs.',
        direction: 'ingest',
        transport: 'rest',
        status: 'coming_soon',
        authType: 'oauth2',
        fields: [
            { key: 'base_url', label: 'Epic FHIR Base URL', type: 'text', required: true },
            { key: 'client_id', label: 'App Client ID', type: 'text', required: true },
            { key: 'private_key', label: 'JWT Signing Key (PEM)', type: 'textarea', required: true },
        ],
    },
    {
        id: 'cerner',
        name: 'Oracle Health (Cerner)',
        category: 'healthcare',
        logo: logo_cerner,
        brandColor: '#C4262E',
        description: 'Clinical data exchange via FHIR R4 (formerly Cerner).',
        direction: 'ingest',
        transport: 'rest',
        status: 'coming_soon',
        authType: 'oauth2',
        fields: [
            { key: 'base_url', label: 'FHIR Base URL', type: 'text', required: true },
            { key: 'client_id', label: 'Client ID', type: 'text', required: true },
            { key: 'client_secret', label: 'Client Secret', type: 'password', required: true },
        ],
    },
    {
        id: 'availity',
        name: 'Availity',
        category: 'healthcare',
        logo: logo_availity,
        brandColor: '#F15A24',
        description: 'Claims, eligibility, and prior authorization processing.',
        direction: 'both',
        transport: 'rest',
        status: 'coming_soon',
        authType: 'oauth2',
        fields: [
            { key: 'client_id', label: 'Client ID', type: 'text', required: true },
            { key: 'client_secret', label: 'Client Secret', type: 'password', required: true },
            { key: 'payer_id', label: 'Payer ID', type: 'text', required: false },
        ],
    },
    {
        id: 'change_healthcare',
        name: 'Change Healthcare',
        category: 'healthcare',
        logo: logo_change_healthcare,
        brandColor: '#0072C6',
        description: 'Claims processing, eligibility, and healthcare analytics.',
        direction: 'both',
        transport: 'rest',
        status: 'coming_soon',
        authType: 'api_key',
        fields: [
            { key: 'client_id', label: 'Client ID', type: 'text', required: true },
            { key: 'client_secret', label: 'Client Secret', type: 'password', required: true },
        ],
    },

    // ═══════════════════════════════════════════
    // Databases
    // ═══════════════════════════════════════════
    {
        id: 'postgresql',
        name: 'PostgreSQL',
        category: 'database',
        logo: logo_postgresql,
        brandColor: '#336791',
        description: 'Table and column schema monitoring via direct connection.',
        direction: 'both',
        transport: 'direct',
        status: 'available',
        authType: 'basic',
        fields: [
            { key: 'host', label: 'Host', type: 'text', required: true },
            { key: 'port', label: 'Port', type: 'number', required: false, placeholder: '5432' },
            { key: 'database', label: 'Database', type: 'text', required: true },
            { key: 'username', label: 'Username', type: 'text', required: true },
            { key: 'password', label: 'Password', type: 'password', required: true },
            { key: 'schema', label: 'Schema', type: 'text', required: false, placeholder: 'public' },
            {
                key: 'ssl', label: 'SSL Mode', type: 'select', required: false, options: [
                    { value: 'disable', label: 'Disable' },
                    { value: 'require', label: 'Require' },
                    { value: 'verify-full', label: 'Verify Full' },
                ]
            },
        ],
    },
    {
        id: 'mysql',
        name: 'MySQL',
        category: 'database',
        logo: logo_mysql,
        brandColor: '#4479A1',
        description: 'Table schema monitoring via direct connection.',
        direction: 'both',
        transport: 'direct',
        status: 'coming_soon',
        authType: 'basic',
        fields: [
            { key: 'host', label: 'Host', type: 'text', required: true },
            { key: 'port', label: 'Port', type: 'number', required: false, placeholder: '3306' },
            { key: 'database', label: 'Database', type: 'text', required: true },
            { key: 'username', label: 'Username', type: 'text', required: true },
            { key: 'password', label: 'Password', type: 'password', required: true },
        ],
    },
    {
        id: 'mongodb',
        name: 'MongoDB',
        category: 'database',
        logo: logo_mongodb,
        brandColor: '#47A248',
        description: 'Collection schema and document structure monitoring.',
        direction: 'both',
        transport: 'direct',
        status: 'coming_soon',
        authType: 'basic',
        fields: [
            { key: 'connection_string', label: 'Connection String', type: 'password', required: true, placeholder: 'mongodb+srv://...' },
            { key: 'database', label: 'Database', type: 'text', required: true },
            { key: 'collection', label: 'Collection', type: 'text', required: false },
        ],
    },
    {
        id: 'snowflake',
        name: 'Snowflake',
        category: 'database',
        logo: logo_snowflake,
        brandColor: '#29B5E8',
        description: 'Table and view schema monitoring in Snowflake.',
        direction: 'both',
        transport: 'direct',
        status: 'coming_soon',
        authType: 'basic',
        fields: [
            { key: 'account', label: 'Account Identifier', type: 'text', required: true, placeholder: 'org-account' },
            { key: 'username', label: 'Username', type: 'text', required: true },
            { key: 'password', label: 'Password', type: 'password', required: true },
            { key: 'warehouse', label: 'Warehouse', type: 'text', required: true },
            { key: 'database', label: 'Database', type: 'text', required: true },
            { key: 'schema', label: 'Schema', type: 'text', required: false, placeholder: 'PUBLIC' },
        ],
    },
    {
        id: 'bigquery',
        name: 'BigQuery',
        category: 'database',
        logo: logo_bigquery,
        brandColor: '#4285F4',
        description: 'Dataset and table schema monitoring in BigQuery.',
        direction: 'both',
        transport: 'direct',
        status: 'coming_soon',
        authType: 'api_key',
        fields: [
            { key: 'project_id', label: 'Project ID', type: 'text', required: true },
            { key: 'dataset_id', label: 'Dataset ID', type: 'text', required: true },
            { key: 'service_account_json', label: 'Service Account JSON', type: 'textarea', required: true },
        ],
    },
    {
        id: 'redshift',
        name: 'Amazon Redshift',
        category: 'database',
        logo: logo_redshift,
        brandColor: '#8C4FFF',
        description: 'Table schema monitoring in Amazon Redshift clusters.',
        direction: 'both',
        transport: 'direct',
        status: 'coming_soon',
        authType: 'basic',
        fields: [
            { key: 'host', label: 'Cluster Endpoint', type: 'text', required: true },
            { key: 'port', label: 'Port', type: 'number', required: false, placeholder: '5439' },
            { key: 'database', label: 'Database', type: 'text', required: true },
            { key: 'username', label: 'Username', type: 'text', required: true },
            { key: 'password', label: 'Password', type: 'password', required: true },
        ],
    },

    // ═══════════════════════════════════════════
    // Logistics / Carriers
    // ═══════════════════════════════════════════
    {
        id: 'fedex',
        name: 'FedEx',
        category: 'logistics',
        logo: logo_fedex,
        brandColor: '#4D148C',
        description: 'Shipping, tracking, and rate data via FedEx API.',
        direction: 'both',
        transport: 'rest',
        status: 'coming_soon',
        authType: 'oauth2',
        fields: [
            { key: 'client_id', label: 'API Key', type: 'text', required: true },
            { key: 'client_secret', label: 'Secret Key', type: 'password', required: true },
            { key: 'account_number', label: 'Account Number', type: 'text', required: true },
        ],
    },
    {
        id: 'ups',
        name: 'UPS',
        category: 'logistics',
        logo: logo_ups,
        brandColor: '#351C15',
        description: 'Shipping, tracking, and address validation via UPS API.',
        direction: 'both',
        transport: 'rest',
        status: 'coming_soon',
        authType: 'oauth2',
        fields: [
            { key: 'client_id', label: 'Client ID', type: 'text', required: true },
            { key: 'client_secret', label: 'Client Secret', type: 'password', required: true },
            { key: 'account_number', label: 'Account Number', type: 'text', required: true },
        ],
    },
    {
        id: 'usps',
        name: 'USPS',
        category: 'logistics',
        logo: logo_usps,
        brandColor: '#333366',
        description: 'Shipping and address verification via USPS Web Tools.',
        direction: 'ingest',
        transport: 'rest',
        status: 'coming_soon',
        authType: 'api_key',
        fields: [
            { key: 'user_id', label: 'USPS Web Tools User ID', type: 'text', required: true },
        ],
    },
    {
        id: 'dhl',
        name: 'DHL',
        category: 'logistics',
        logo: logo_dhl,
        brandColor: '#FFCC00',
        description: 'Express and eCommerce shipping and tracking via DHL API.',
        direction: 'both',
        transport: 'rest',
        status: 'coming_soon',
        authType: 'api_key',
        fields: [
            { key: 'api_key', label: 'API Key', type: 'password', required: true },
            { key: 'site_id', label: 'Site ID', type: 'text', required: false },
        ],
    },
    {
        id: 'easypost',
        name: 'EasyPost',
        category: 'logistics',
        logo: logo_easypost,
        brandColor: '#3B82F6',
        description: 'Multi-carrier rate comparison, labels, and tracking.',
        direction: 'both',
        transport: 'rest',
        status: 'coming_soon',
        authType: 'api_key',
        fields: [
            { key: 'api_key', label: 'API Key', type: 'password', required: true },
        ],
    },

    // ═══════════════════════════════════════════
    // More E-Commerce / Marketplaces
    // ═══════════════════════════════════════════
    {
        id: 'ebay',
        name: 'eBay',
        category: 'ecommerce',
        logo: logo_ebay,
        brandColor: '#E53238',
        description: 'Orders, listings, and inventory via eBay API.',
        direction: 'both',
        transport: 'rest',
        status: 'coming_soon',
        authType: 'oauth2',
        fields: [
            { key: 'client_id', label: 'App ID (Client ID)', type: 'text', required: true },
            { key: 'client_secret', label: 'Cert ID (Client Secret)', type: 'password', required: true },
            { key: 'refresh_token', label: 'Refresh Token', type: 'password', required: true },
        ],
    },
    {
        id: 'etsy',
        name: 'Etsy',
        category: 'ecommerce',
        logo: logo_etsy,
        brandColor: '#F1641E',
        description: 'Orders, listings, and shop data via Etsy Open API.',
        direction: 'both',
        transport: 'rest',
        status: 'coming_soon',
        authType: 'oauth2',
        fields: [
            { key: 'api_key', label: 'API Key', type: 'text', required: true },
            { key: 'shared_secret', label: 'Shared Secret', type: 'password', required: true },
        ],
    },
    {
        id: 'walmart',
        name: 'Walmart Marketplace',
        category: 'ecommerce',
        logo: logo_walmart,
        brandColor: '#0071CE',
        description: 'Orders, inventory, and items via Seller Center API.',
        direction: 'both',
        transport: 'rest',
        status: 'coming_soon',
        authType: 'api_key',
        fields: [
            { key: 'client_id', label: 'Client ID', type: 'text', required: true },
            { key: 'client_secret', label: 'Client Secret', type: 'password', required: true },
        ],
    },

    // ═══════════════════════════════════════════
    // More EDI / VAN
    // ═══════════════════════════════════════════
    {
        id: 'commport',
        name: 'Commport',
        category: 'edi_van',
        logo: logo_commport,
        brandColor: '#003F87',
        description: 'Canadian EDI VAN and supply chain connectivity.',
        direction: 'both',
        transport: 'as2',
        status: 'available',
        authType: 'certificate',
        fields: [],
    },
    {
        id: 'inovis',
        name: 'OpenText Trading Grid',
        category: 'edi_van',
        logo: logo_inovis,
        brandColor: '#4A90D9',
        description: 'Global B2B trading network (formerly GXS/Inovis).',
        direction: 'both',
        transport: 'as2',
        status: 'available',
        authType: 'certificate',
        fields: [],
    },
    {
        id: 'kleinschmidt',
        name: 'Kleinschmidt (1EDI)',
        category: 'edi_van',
        logo: logo_kleinschmidt,
        brandColor: '#1a5276',
        description: 'Managed EDI services for healthcare and retail.',
        direction: 'both',
        transport: 'sftp',
        status: 'available',
        authType: 'basic',
        fields: [
            { key: 'host', label: 'SFTP Host', type: 'text', required: true },
            { key: 'username', label: 'Username', type: 'text', required: true },
            { key: 'password', label: 'Password', type: 'password', required: true },
        ],
    },

    // ═══════════════════════════════════════════
    // More WMS / 3PL
    // ═══════════════════════════════════════════
    {
        id: 'shiphero',
        name: 'ShipHero',
        category: 'wms',
        logo: logo_shiphero,
        brandColor: '#2563EB',
        description: 'WMS and 3PL fulfillment for ecommerce brands.',
        direction: 'both',
        transport: 'rest',
        status: 'coming_soon',
        authType: 'api_key',
        fields: [
            { key: 'access_token', label: 'Access Token', type: 'password', required: true },
        ],
    },
    {
        id: 'skubana',
        name: 'Extensiv (Skubana)',
        category: 'wms',
        logo: logo_skubana,
        brandColor: '#FF6A00',
        description: 'Multichannel inventory and order management.',
        direction: 'both',
        transport: 'rest',
        status: 'coming_soon',
        authType: 'api_key',
        fields: [
            { key: 'api_key', label: 'API Key', type: 'password', required: true },
        ],
    },

    // ═══════════════════════════════════════════
    // More Notifications / Collaboration
    // ═══════════════════════════════════════════
    {
        id: 'discord',
        name: 'Discord',
        category: 'notification',
        logo: logo_discord,
        brandColor: '#5865F2',
        description: 'Drift alerts delivered to Discord channels via webhook.',
        direction: 'export',
        transport: 'webhook',
        status: 'available',
        authType: 'api_key',
        fields: [
            { key: 'webhook_url', label: 'Discord Webhook URL', type: 'text', required: true, placeholder: 'https://discord.com/api/webhooks/...' },
        ],
    },
    {
        id: 'opsgenie',
        name: 'OpsGenie',
        category: 'notification',
        logo: logo_opsgenie,
        brandColor: '#172B4D',
        description: 'Escalation and incident management via OpsGenie API.',
        direction: 'export',
        transport: 'rest',
        status: 'available',
        authType: 'api_key',
        fields: [
            { key: 'api_key', label: 'API Key', type: 'password', required: true },
            {
                key: 'region', label: 'Region', type: 'select', required: false, options: [
                    { value: 'us', label: 'US' },
                    { value: 'eu', label: 'EU' },
                ]
            },
        ],
    },
    {
        id: 'twilio',
        name: 'Twilio SMS',
        category: 'notification',
        logo: logo_twilio,
        brandColor: '#F22F46',
        description: 'SMS alerts on critical drift events via Twilio API.',
        direction: 'export',
        transport: 'rest',
        status: 'available',
        authType: 'api_key',
        fields: [
            { key: 'account_sid', label: 'Account SID', type: 'text', required: true },
            { key: 'auth_token', label: 'Auth Token', type: 'password', required: true },
            { key: 'from_number', label: 'From Number', type: 'text', required: true, placeholder: '+1234567890' },
            { key: 'to_number', label: 'To Number', type: 'text', required: true, placeholder: '+1234567890' },
        ],
    },

    // ═══════════════════════════════════════════
    // More ERP / Finance
    // ═══════════════════════════════════════════
    {
        id: 'sage',
        name: 'Sage Intacct',
        category: 'erp',
        logo: logo_sage,
        brandColor: '#00DC82',
        description: 'Cloud financials via Sage Intacct Web Services.',
        direction: 'both',
        transport: 'rest',
        status: 'coming_soon',
        authType: 'basic',
        fields: [
            { key: 'company_id', label: 'Company ID', type: 'text', required: true },
            { key: 'user_id', label: 'User ID', type: 'text', required: true },
            { key: 'user_password', label: 'User Password', type: 'password', required: true },
            { key: 'sender_id', label: 'Web Services Sender ID', type: 'text', required: true },
            { key: 'sender_password', label: 'Sender Password', type: 'password', required: true },
        ],
    },
    {
        id: 'freshbooks',
        name: 'FreshBooks',
        category: 'erp',
        logo: logo_freshbooks,
        brandColor: '#00B2EE',
        description: 'Invoicing, expenses, and time tracking via FreshBooks API.',
        direction: 'both',
        transport: 'rest',
        status: 'coming_soon',
        authType: 'oauth2',
        fields: [
            { key: 'client_id', label: 'Client ID', type: 'text', required: true },
            { key: 'client_secret', label: 'Client Secret', type: 'password', required: true },
        ],
    },

    // ═══════════════════════════════════════════
    // More Dev / Ops
    // ═══════════════════════════════════════════
    {
        id: 'splunk',
        name: 'Splunk',
        category: 'devops',
        logo: logo_splunk,
        brandColor: '#65A637',
        description: 'Drift events forwarded to Splunk via HTTP Event Collector.',
        direction: 'export',
        transport: 'rest',
        status: 'available',
        authType: 'api_key',
        fields: [
            { key: 'hec_url', label: 'HEC Endpoint URL', type: 'text', required: true, placeholder: 'https://splunk:8088/services/collector' },
            { key: 'hec_token', label: 'HEC Token', type: 'password', required: true },
            { key: 'index', label: 'Index', type: 'text', required: false, placeholder: 'main' },
        ],
    },
    {
        id: 'grafana',
        name: 'Grafana',
        category: 'devops',
        logo: logo_grafana,
        brandColor: '#F46800',
        description: 'Drift metrics pushed to Grafana Cloud or self-hosted.',
        direction: 'export',
        transport: 'rest',
        status: 'available',
        authType: 'api_key',
        fields: [
            { key: 'url', label: 'Grafana URL', type: 'text', required: true },
            { key: 'api_key', label: 'API Key', type: 'password', required: true },
        ],
    },
    {
        id: 'newrelic',
        name: 'New Relic',
        category: 'devops',
        logo: logo_newrelic,
        brandColor: '#008C99',
        description: 'Custom events and metrics forwarded to New Relic.',
        direction: 'export',
        transport: 'rest',
        status: 'available',
        authType: 'api_key',
        fields: [
            { key: 'api_key', label: 'Insert API Key', type: 'password', required: true },
            { key: 'account_id', label: 'Account ID', type: 'text', required: true },
        ],
    },
    {
        id: 'linear',
        name: 'Linear',
        category: 'devops',
        logo: logo_linear,
        brandColor: '#5E6AD2',
        description: 'Auto-create Linear issues on drift events.',
        direction: 'export',
        transport: 'rest',
        status: 'available',
        authType: 'api_key',
        fields: [
            { key: 'api_key', label: 'API Key', type: 'password', required: true },
            { key: 'team_id', label: 'Team ID', type: 'text', required: true },
        ],
    },
    {
        id: 'zapier',
        name: 'Zapier',
        category: 'devops',
        logo: logo_zapier,
        brandColor: '#FF4A00',
        description: 'Trigger Zapier workflows on drift events.',
        direction: 'export',
        transport: 'webhook',
        status: 'available',
        authType: 'api_key',
        fields: [
            { key: 'webhook_url', label: 'Zapier Webhook URL', type: 'text', required: true, placeholder: 'https://hooks.zapier.com/hooks/catch/...' },
        ],
    },
    {
        id: 'make',
        name: 'Make (Integromat)',
        category: 'devops',
        logo: logo_make,
        brandColor: '#6D00CC',
        description: 'Trigger Make scenarios on drift events.',
        direction: 'export',
        transport: 'webhook',
        status: 'available',
        authType: 'api_key',
        fields: [
            { key: 'webhook_url', label: 'Make Webhook URL', type: 'text', required: true },
        ],
    },
];

// ─── Helper Functions ─────────────────────────────────────────────────────────

/** Get all connectors that can be used as ingest sources */
export const getIngestConnectors = () =>
    CONNECTORS.filter(c => c.direction === 'ingest' || c.direction === 'both');

/** Get all connectors that can be used as export destinations */
export const getExportConnectors = () =>
    CONNECTORS.filter(c => c.direction === 'export' || c.direction === 'both');

/** Get a connector by ID */
export const getConnectorById = (id) =>
    CONNECTORS.find(c => c.id === id);

/** Get connectors for a specific category */
export const getConnectorsByCategory = (categoryId) =>
    CONNECTORS.filter(c => c.category === categoryId);

/** Get the category object by ID */
export const getCategoryById = (categoryId) =>
    CONNECTOR_CATEGORIES.find(c => c.id === categoryId);

/** Check if a connector has a working backend adapter */
export const isConnectorAvailable = (connectorId) => {
    const c = getConnectorById(connectorId);
    return c?.status === 'available';
};
